/**
 * What the browser keeps (ADR-29): the enrolled device (id + secret, "something you have") survives restarts;
 * the session token lives only for this tab and dies with the idle lock. The PIN is never stored.
 */

export interface DeviceCreds {
  deviceId: string;
  deviceSecret: string;
  door: "customer" | "staff";
  displayName: string;
  pinLength: 4 | 6;
}

export interface SessionInfo {
  token: string;
  context: "customer" | "staff";
}

const DEVICE_KEY = "kitchen.device";
const SESSION_KEY = "kitchen.session";

// The stored JSON shape is named by the caller.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
function read<T>(storage: () => Storage, key: string): T | null {
  try {
    const raw = storage().getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function write(storage: () => Storage, key: string, value: unknown): void {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or blocked storage: the user simply signs in again next time.
  }
}

const TEST_DEVICE_KEY = "kitchen.test-device";

/**
 * Staging test logins (AS-061): a link `#test=<base64url JSON>` made by `cli.js test-logins` opens one role in
 * this tab only. The device stays in sessionStorage, so other tabs (and the real device) are not touched.
 */
export function adoptTestLogin(): void {
  const m = /^#test=([A-Za-z0-9_-]+)$/.exec(location.hash);
  if (!m?.[1]) return;
  history.replaceState(null, "", location.pathname + location.search);
  try {
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(m[1].replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0)),
    );
    const t = JSON.parse(json) as {
      token: string;
      context: "customer" | "staff";
      deviceId: string;
      name: string;
    };
    write(() => sessionStorage, TEST_DEVICE_KEY, {
      deviceId: t.deviceId,
      deviceSecret: "",
      door: t.context,
      displayName: t.name,
      pinLength: 4,
    } satisfies DeviceCreds);
    write(() => sessionStorage, SESSION_KEY, { token: t.token, context: t.context } satisfies SessionInfo);
  } catch {
    // A broken link: the normal sign-in page shows.
  }
}

export const loadDevice = () =>
  read<DeviceCreds>(() => sessionStorage, TEST_DEVICE_KEY) ??
  read<DeviceCreds>(() => localStorage, DEVICE_KEY);
export const saveDevice = (d: DeviceCreds | null) => {
  write(() => localStorage, DEVICE_KEY, d);
};
export const loadSession = () => read<SessionInfo>(() => sessionStorage, SESSION_KEY);
export const saveSession = (s: SessionInfo | null) => {
  write(() => sessionStorage, SESSION_KEY, s);
};
